import React, { useEffect, useState, useCallback } from 'react';

const ACCENT = '#4A7C59';

interface DryingBed {
  id: string;
  name: string;
  collectionCentreId: string | null;
  managerId: string | null;
  managerName: string | null;
  managerPhone: string | null;
  capacityKg: string | null;
  active: boolean;
  createdAt: string;
}

interface ActiveLog {
  id: string;
  batchNumber: string;
  batchId: string;
  bedName: string | null;
  startDate: string;
  initialMoisturePct: string | null;
  targetMoisturePct: string | null;
  daysOnBed: number;
}

interface ManagerStaff {
  id: string;
  name: string;
  duty: string;
  phone: string | null;
}

export const DryingBedsView: React.FC = () => {
  const [beds, setBeds] = useState<DryingBed[]>([]);
  const [activeLogs, setActiveLogs] = useState<ActiveLog[]>([]);
  const [managers, setManagers] = useState<ManagerStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', capacityKg: '', managerId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchBeds = useCallback(async () => {
    setLoading(true);
    try {
      const [bedsRes, logsRes, staffRes] = await Promise.all([
        fetch('/api/production/drying?resource=beds', { credentials: 'include' }),
        fetch('/api/production/drying?resource=active', { credentials: 'include' }),
        fetch('/api/production/hub-staff?status=active', { credentials: 'include' }),
      ]);
      const bedsData = await bedsRes.json();
      const logsData = await logsRes.json();
      const staffData = await staffRes.json();
      setBeds(bedsData.beds || []);
      setActiveLogs(logsData.logs || []);
      setManagers(staffData.staff || []);
    } catch {
      setBeds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBeds(); }, [fetchBeds]);

  const createBed = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/production/drying?resource=beds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Failed');
        return;
      }
      const bed = await res.json();
      // Attach manager name locally so card renders immediately
      const mgr = managers.find(m => m.id === form.managerId);
      setBeds(b => [...b, { ...bed, managerName: mgr?.name || null }]);
      setForm({ name: '', capacityKg: '', managerId: '' });
      setShowForm(false);
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const toggleBed = async (bed: DryingBed) => {
    const res = await fetch(`/api/production/drying?resource=beds&id=${bed.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ active: !bed.active }),
    });
    if (res.ok) {
      setBeds(b => b.map(x => x.id === bed.id ? { ...x, active: !bed.active } : x));
    }
  };

  const inputCls = "w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none";
  const foc = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blu = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.boxShadow = '');

  const activeBeds = beds.filter(b => b.active);
  const inactiveBeds = beds.filter(b => !b.active);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Drying beds</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">
            {activeBeds.length} active · {activeLogs.length} batches currently drying
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2.5 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          + Add bed
        </button>
      </div>

      {/* Add bed form */}
      {showForm && (
        <form onSubmit={createBed} className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-6 space-y-4">
          <h4 className="font-semibold text-[#1A1A1A]">New drying bed</h4>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#6B6B6B] mb-1.5">Bed name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="e.g. Bed A1"
                className={inputCls}
                onFocus={foc}
                onBlur={blu}
              />
            </div>
            <div>
              <label className="block text-xs text-[#6B6B6B] mb-1.5">Capacity (kg)</label>
              <input
                type="number"
                value={form.capacityKg}
                onChange={e => setForm(f => ({ ...f, capacityKg: e.target.value }))}
                placeholder="e.g. 500"
                step="0.1"
                min="0"
                className={inputCls}
                onFocus={foc}
                onBlur={blu}
              />
            </div>
          </div>
          {managers.length > 0 && (
            <div>
              <label className="block text-xs text-[#6B6B6B] mb-1.5">Bed manager</label>
              <select
                value={form.managerId}
                onChange={e => setForm(f => ({ ...f, managerId: e.target.value }))}
                className={inputCls}
                onFocus={foc}
                onBlur={blu}
              >
                <option value="">No manager assigned</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}{m.phone ? ` — ${m.phone}` : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {saving ? 'Saving...' : 'Save bed'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* Active batches on beds */}
          {activeLogs.length > 0 && (
            <section>
              <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">Currently drying</h3>
              <div className="space-y-2">
                {activeLogs.map(log => (
                  <div key={log.id} className="bg-white border border-[#E8E6E2] rounded-xl p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-sm font-medium text-[#1A1A1A]">{log.batchNumber}</span>
                        {log.bedName && (
                          <span className="text-xs text-[#6B6B6B] bg-[#F0EDE8] px-2 py-0.5 rounded">{log.bedName}</span>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-[#6B6B6B]">
                        <span>Started {new Date(log.startDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short' })}</span>
                        {log.initialMoisturePct && <span>Initial: {log.initialMoisturePct}%</span>}
                        {log.targetMoisturePct && <span>Target: {log.targetMoisturePct}%</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold" style={{ color: ACCENT }}>{log.daysOnBed}</p>
                      <p className="text-xs text-[#A3A3A3]">day{log.daysOnBed !== 1 ? 's' : ''}</p>
                    </div>
                    <a
                      href={`/production/batches/${log.batchId}`}
                      className="text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] shrink-0"
                    >
                      View →
                    </a>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Active beds */}
          <section>
            <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">
              Active beds ({activeBeds.length})
            </h3>
            {activeBeds.length === 0 ? (
              <div className="py-8 text-center text-sm text-[#A3A3A3] border border-dashed border-[#E8E6E2] rounded-xl">
                No active beds — add one above
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {activeBeds.map(bed => {
                  const inUse = activeLogs.filter(l => l.bedName === bed.name).length;
                  return (
                    <div key={bed.id} className="bg-white border border-[#E8E6E2] rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold text-sm text-[#1A1A1A]">{bed.name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          inUse > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {inUse > 0 ? 'In use' : 'Free'}
                        </span>
                      </div>
                      {bed.capacityKg && (
                        <p className="text-xs text-[#A3A3A3]">{parseFloat(bed.capacityKg).toLocaleString()} kg capacity</p>
                      )}
                      {bed.managerName && (
                        <div className="mt-0.5">
                          <p className="text-xs text-[#6B6B6B]">{bed.managerName}</p>
                          {bed.managerPhone && <p className="text-xs text-[#A3A3A3]">{bed.managerPhone}</p>}
                        </div>
                      )}
                      <button
                        onClick={() => toggleBed(bed)}
                        className="mt-3 text-xs text-[#A3A3A3] hover:text-red-600 transition-colors"
                      >
                        Deactivate
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Inactive beds */}
          {inactiveBeds.length > 0 && (
            <section>
              <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">
                Inactive beds ({inactiveBeds.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {inactiveBeds.map(bed => (
                  <div key={bed.id} className="bg-[#F8F7F5] border border-[#E8E6E2] rounded-xl p-4 opacity-60">
                    <p className="font-semibold text-sm text-[#1A1A1A] mb-1">{bed.name}</p>
                    {bed.capacityKg && (
                      <p className="text-xs text-[#A3A3A3]">{parseFloat(bed.capacityKg).toLocaleString()} kg</p>
                    )}
                    {bed.managerName && (
                      <p className="text-xs text-[#6B6B6B] mt-0.5">{bed.managerName}</p>
                    )}
                    <button
                      onClick={() => toggleBed(bed)}
                      className="mt-3 text-xs font-medium hover:text-[#4A7C59] transition-colors"
                      style={{ color: ACCENT }}
                    >
                      Reactivate
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};
